import React, { PropsWithChildren, ReactElement, CSSProperties } from 'react';

import styled from 'styled-components';

import CustomIcon from '@components/utils/CustomIcon';
import arrowRight from '@assets/icons/icon-arrow-right.svg';
import arrowDown from '@assets/icons/icon-arrow-down.svg';
import { SectionHeading } from '@shared/SectionHeading';

export enum ArrowAlign {
  NO_ARROW = '0',
  UP_DOWN = '1',
  FIXED_RIGHT = '2',
}

interface ShowMoreCardProps {
  title: string;
  navigationText: string | ReactElement;
  onNavigate?: () => void;
  navigationStyle?: CSSProperties;
  isShowMore?: boolean;
  arrowAlign?: ArrowAlign;
  containerStyle?: CSSProperties;
  isShowAll?: boolean;
}

const Container = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.palette.grey1100};
  position: relative;
  white-space: nowrap;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Title = styled(SectionHeading)`
  margin-bottom: 0.5em;
`;

const NavigationText = styled.button`
  display: flex;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grey1100};
  border-radius: 6px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.palette.whiteAlpha100};
  border: ${({ theme }) => `1px solid ${theme.palette.grey300}`};
  cursor: pointer;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const Navigation = styled.div`
  margin: 0 20px 0 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const Children = styled.div`
  width: 100%;
  margin-top: 24px;
  white-space: nowrap;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    margin-top: 10px;
  }
`;

const Arrow = styled.div`
  margin-left: 5px;
`;

const ShowMoreCard = ({
  title,
  navigationText,
  onNavigate,
  navigationStyle,
  children,
  isShowMore = true,
  arrowAlign = ArrowAlign.NO_ARROW,
  containerStyle,
}: PropsWithChildren<ShowMoreCardProps>) => {
  return (
    <Container {...(containerStyle ? { style: containerStyle } : {})}>
      <Wrapper>
        <Title>{title}</Title>
      </Wrapper>

      <>
        <Children>{children}</Children>
        {onNavigate && navigationText ? (
          <Navigation style={navigationStyle}>
            <NavigationText onClick={onNavigate}>
              {navigationText}
              {arrowAlign === ArrowAlign.NO_ARROW ? null : (
                <Arrow>
                  <CustomIcon
                    isOpen={ArrowAlign.UP_DOWN && isShowMore}
                    icon={
                      arrowAlign === ArrowAlign.UP_DOWN ? arrowDown : arrowRight
                    }
                    fontSize={20}
                  />
                </Arrow>
              )}
            </NavigationText>
          </Navigation>
        ) : null}
      </>
    </Container>
  );
};

export default ShowMoreCard;
